import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pb } from '@/core/api';
import { useToast } from '@/components/ui/toast';

export type GroupTier = {
  id: string;
  min_avg: number;
  max_avg: number | null;
  amount: number;
  sort_order: number;
};

export type GroupTierInput = Omit<GroupTier, 'id'>;

const QK = ['group-bonus-tiers'];
const COLLECTION = 'group_bonus_tiers';

type PBRecord = { max_avg?: unknown } & Record<string, unknown>;

function normalize(rec: PBRecord): GroupTier {
  const raw = rec.max_avg;
  const max = typeof raw === 'number' && raw > 0 ? raw : null;
  return {
    id: String(rec['id']),
    min_avg: Number(rec['min_avg']) || 0,
    max_avg: max,
    amount: Number(rec['amount']) || 0,
    sort_order: Number(rec['sort_order']) || 0,
  };
}

export function useGroupTiers(): {
  data: GroupTier[];
  isLoading: boolean;
} {
  const q = useQuery({
    queryKey: QK,
    queryFn: async (): Promise<GroupTier[]> => {
      const recs = await pb.collection(COLLECTION).getFullList<PBRecord>();
      return recs.map(normalize).sort((a, b) => a.sort_order - b.sort_order);
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
  return { data: q.data ?? [], isLoading: q.isLoading };
}

function toPayload(t: GroupTierInput): Record<string, unknown> {
  return {
    min_avg: t.min_avg,
    max_avg: t.max_avg ?? 0,
    amount: t.amount,
    sort_order: t.sort_order,
  };
}

export function useCreateGroupTier() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (t: GroupTierInput) =>
      pb.collection(COLLECTION).create(toPayload(t)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK });
      toast('Tramo creado', 'success');
    },
    onError: () => toast('Error al crear tramo', 'error'),
  });
}

export function useUpdateGroupTier() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (v: { id: string; data: GroupTierInput }) =>
      pb.collection(COLLECTION).update(v.id, toPayload(v.data)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK });
      toast('Tramo actualizado', 'success');
    },
    onError: () => toast('Error al actualizar tramo', 'error'),
  });
}

export function useDeleteGroupTier() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => pb.collection(COLLECTION).delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK });
      toast('Tramo eliminado', 'success');
    },
    onError: () => toast('Error al eliminar tramo', 'error'),
  });
}
